/**
 * IT DECONFIG TASK DETAIL PAGE
 *
 * IT team deconfiguration page for location deactivation.
 * Allows IT team to confirm deconfiguration steps and complete the task.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/status-badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MapPin, XCircle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { Task, Organization, Location } from '@/types/database';

interface TaskWithRelations extends Task {
  organization: Pick<Organization, 'id' | 'name' | 'status' | 'logo_url'> | null;
  location: Location | null;
}

function ITDeconfigTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [task, setTask] = useState<TaskWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<any>(null);
  
  // Deconfiguration checklist
  const [checklist, setChecklist] = useState({
    removedInfrastructure: false,
    revokedAccess: false,
    cleanedConfigs: false,
    documentedChanges: false,
  });

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'IT_TEAM' && user.role !== 'IT_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) {
        navigate('/it-tasks');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch task with organization and location details
        const { data: taskData, error: taskError } = await supabase
          .from('tasks')
          .select(`
            *,
            organization:organizations(id, name, status, logo_url),
            location:locations(
              id,
              name,
              description,
              logo_url,
              source_schema,
              open_dental_source_schema,
              open_dental_clinic,
              source_name,
              status,
              mapping_status
            )
          `)
          .eq('id', taskId)
          .eq('type', 'IT_DECONFIG')
          .single();

        if (taskError) {
          console.error('Error fetching task:', taskError);
          navigate('/it-tasks');
          return;
        }

        if (!taskData) {
          navigate('/it-tasks');
          return;
        }

        setTask(taskData as TaskWithRelations);

        // Fetch onboarding_queue data separately using organization ID
        if (taskData.organization_id) {
          const { data: queueData } = await supabase
            .from('onboarding_queue')
            .select('primary_contact_name, primary_contact_email, phone_number')
            .eq('id', taskData.organization_id)
            .maybeSingle();

          if (queueData) {
            setContactInfo(queueData);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [taskId, navigate]);

  const allChecklistComplete = Object.values(checklist).every(Boolean);

  const handleComplete = async () => {
    if (!task || !allChecklistComplete) return;

    try {
      setCompleting(true);
      setError(null);
      const supabase = createClient();

      // Mark task as complete
      const { error: taskError } = await supabase
        .from('tasks')
        .update({
          status: 'COMPLETED',
          completed_at: new Date().toISOString(),
          completed_by: user.id,
          metadata: {
            ...task.metadata,
            checklist,
            completed_steps: Object.keys(checklist).filter(key => checklist[key as keyof typeof checklist]),
          },
        })
        .eq('id', task.id);

      if (taskError) throw taskError;

      // Check if this is a deletion queue task
      const deletionQueueId = task.metadata?.deletion_queue_id;
      
      if (deletionQueueId && task.location_id) {
        // This is a deletion task - fetch the deletion queue to check stage
        const { data: deletionQueue } = await supabase
          .from('location_onboarding_deletion_queue')
          .select('deletion_stage')
          .eq('id', deletionQueueId)
          .single();

        if (!deletionQueue) {
          console.error('Deletion queue not found for ID:', deletionQueueId);
        } else {
          // Check if DATA_DECONFIG task exists
          const { data: dataDeconfigTask } = await supabase
            .from('tasks')
            .select('id, status')
            .eq('location_id', task.location_id)
            .eq('type', 'DATA_DECONFIG')
            .maybeSingle();

          const shouldDeleteLocation = 
            (deletionQueue.deletion_stage === 'AWAITING_IT_DECONFIG' && !dataDeconfigTask) ||
            (deletionQueue.deletion_stage === 'AWAITING_BOTH_DECONFIG' && dataDeconfigTask?.status === 'COMPLETED');

          if (shouldDeleteLocation) {
            // Delete the location
            const { error: deleteLocationError } = await supabase
              .from('locations')
              .delete()
              .eq('id', task.location_id);

            if (deleteLocationError) {
              console.error('Failed to delete location:', deleteLocationError);
              throw new Error(`Failed to delete location: ${deleteLocationError.message}`);
            }

            // Update deletion queue to DELETED
            const { error: updateQueueError } = await supabase
              .from('location_onboarding_deletion_queue')
              .update({
                deletion_stage: 'DELETED',
                deleted_at: new Date().toISOString(),
              })
              .eq('id', deletionQueueId);

            if (updateQueueError) {
              console.error('Failed to update deletion queue:', updateQueueError);
            }
          }
        }
      } else {
        // This is a regular deactivation task
        // Update deactivation queue status
        if (task.metadata?.deactivation_request_id) {
          await supabase
            .from('location_deactivation_queue')
            .update({
              status: 'AWAITING_DATA_DECONFIG',
            })
            .eq('id', task.metadata.deactivation_request_id);
        } else if (task.location_id) {
          await supabase
            .from('location_deactivation_queue')
            .update({
              status: 'AWAITING_DATA_DECONFIG',
            })
            .eq('location_id', task.location_id);
        }

        // Create DATA_DECONFIG task
        await supabase.from('tasks').insert({
          organization_id: task.organization_id,
          location_id: task.location_id,
          type: 'DATA_DECONFIG',
          title: `Data Deconfiguration: ${task.location?.name}`,
          description: `Deconfigure data pipelines for ${task.location?.name}`,
          status: 'PENDING',
          priority: 1,
          sequence_order: 2,
          metadata: task.metadata,
        });
      }

      // Log activity
      try {
        await supabase.rpc('log_activity', {
          p_user_id: user.id,
          p_action: 'TASK_COMPLETED' as any,
          p_description: `IT deconfiguration completed for ${task.location?.name}`,
          p_organization_id: task.organization_id,
          p_task_id: task.id,
          p_location_id: task.location_id || undefined,
        });
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }

      navigate('/it-tasks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete task');
      setCompleting(false);
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading task</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  const isCompleted = task.status === 'COMPLETED';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/it-tasks')}
        className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to IT Tasks</span>
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {/* Location Logo */}
          {task.location?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                  <img
                    src={task.location.logo_url}
                    alt={`${task.location?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                  <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Header Content */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                DEACTIVATION
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.location?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Location</TooltipContent>
              </Tooltip>
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.organization?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Organization</TooltipContent>
              </Tooltip>
            </p>
            <div className="mt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={task.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Contact Information */}
      {contactInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contactInfo.primary_contact_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Contact Name</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_name}</p>
                </div>
              )}
              {contactInfo.primary_contact_email && (
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_email}</p>
                </div>
              )}
              {contactInfo.phone_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium text-sm">{contactInfo.phone_number}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {task.location?.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.location.description}</p>
            </div>
          )}

          {/* Current Configuration */}
          {(task.location as any)?.open_dental_source_schema && (
            <div className="pt-4 border-t space-y-3">
              <h4 className="text-sm font-semibold">Current Configuration</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">OpenDental Schema</p>
                  <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                    {(task.location as any).open_dental_source_schema}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Clinic Number</p>
                  <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded">
                    {(task.location as any).open_dental_clinic || 'Not set'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deconfiguration Checklist */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>IT Deconfiguration Checklist</CardTitle>
          <CardDescription>
            {isCompleted
              ? 'Deconfiguration completed. All steps were verified.'
              : 'Complete all steps before marking this task as done'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Checkbox
                id="removedInfrastructure"
                checked={checklist.removedInfrastructure}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, removedInfrastructure: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="removedInfrastructure"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Removed infrastructure and server configurations
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="revokedAccess"
                checked={checklist.revokedAccess}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, revokedAccess: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="revokedAccess"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Revoked all access credentials and API keys
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="cleanedConfigs"
                checked={checklist.cleanedConfigs}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, cleanedConfigs: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="cleanedConfigs"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Cleaned up OpenDental and system configurations
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="documentedChanges"
                checked={checklist.documentedChanges}
                onCheckedChange={(checked) =>
                  setChecklist({ ...checklist, documentedChanges: !!checked })
                }
                disabled={isCompleted}
              />
              <label
                htmlFor="documentedChanges"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Documented all deconfiguration changes
              </label>
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-3 rounded-md">
              {error}
            </div>
          )}

          {!isCompleted && (
            <div className="mt-6">
              <Button
                onClick={handleComplete}
                disabled={!allChecklistComplete || completing}
                className="w-full"
                size="lg"
              >
                {completing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Complete IT Deconfiguration
                  </>
                )}
              </Button>
              {!allChecklistComplete && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Complete all checklist items to proceed
                </p>
              )}
            </div>
          )}

          {isCompleted && (
            <div className="mt-6 flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="font-semibold">Deconfiguration Complete</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ITDeconfigTaskDetailPage;
